// Closing-gate Bypass admin console (Escrow gate).
//
// ADMIN-only break-glass surface: force-complete a TEST deal's Escrow Setup
// gate so QA can advance to loan boarding without a real, funded escrow close,
// plus a cross-deal log of recent bypasses. The route is also gated by
// RequireAuth; this page self-gates on the same permission (defence in depth).

import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  FormField,
  Input,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { extractErrorMessage } from "@/api/client";
import { DEAL_FLOW_PERMISSIONS } from "@/lib/dealFlowPermissions";
import {
  useBypassEscrowGateForInput,
  useRecentGateBypasses,
} from "@/hooks/dealFlow/useDealGateBypass";
import { useDealEscrowStatus } from "@/hooks/dealFlow/useDealFlow";
import { escrowBypassBlockedReason } from "@/components/DealFlow/gateBypass/escrowBypassAvailability";
import { useDateFormatter } from "@/utils/datetime";

const GUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const MIN_REASON_LENGTH = 5;

export function AdminBypassConsolePage() {
  const { hasPermission } = useAuth();
  const canBypass = hasPermission(DEAL_FLOW_PERMISSIONS.BypassClosingGate);

  if (!canBypass) {
    return (
      <div className="max-w-reading mx-auto p-space-5 text-center">
        <h2 className="font-display font-bold text-h2">Access denied</h2>
        <p className="text-body text-gray-mid mt-space-3">
          Your role does not have permission to bypass closing gates. Contact
          your administrator if you believe this is a mistake.
        </p>
      </div>
    );
  }

  return <AdminBypassConsole />;
}

function AdminBypassConsole() {
  const recent = useRecentGateBypasses(100);
  const bypass = useBypassEscrowGateForInput();
  const toast = useToast();
  const { relative } = useDateFormatter();

  const [dealId, setDealId] = useState("");
  const [reason, setReason] = useState("");

  const trimmedDealId = dealId.trim();
  const trimmedReason = reason.trim();
  const dealIdValid = GUID_RE.test(trimmedDealId);
  const reasonValid = trimmedReason.length >= MIN_REASON_LENGTH;
  const dealIdTouched = trimmedDealId.length > 0;
  // Owner rulings, 2026-10-09 and 2026-10-26: a deal whose escrow was released,
  // or that has real escrow activity, cannot be bypassed
  // (escrowBypassAvailability.ts).
  const escrow = useDealEscrowStatus(dealIdValid ? trimmedDealId : undefined);
  const blocked = dealIdValid ? escrowBypassBlockedReason(escrow.data) : null;

  const onSubmit = async () => {
    if (!dealIdValid) {
      toast.error("Enter a valid Deal ID (GUID).");
      return;
    }
    if (!reasonValid) {
      toast.error(`Reason must be at least ${MIN_REASON_LENGTH} characters.`);
      return;
    }
    try {
      await bypass.mutateAsync({ dealId: trimmedDealId, reason: trimmedReason });
      toast.success("Escrow gate bypassed for testing.");
      setDealId("");
      setReason("");
      // The mutation invalidates the recent log; refetch to reflect it now.
      void recent.refetch();
    } catch (err) {
      // 404 (deal not found) / 422 (real escrow activity) / 400 messages
      // surface verbatim.
      toast.error(extractErrorMessage(err));
    }
  };

  const rows = recent.data ?? [];

  return (
    <div className="flex flex-col gap-space-5">
      <PageHeader
        eyebrow="Deal Flow · Admin"
        title="Closing-gate Bypass"
        description="Force-completes closing gates for TEST deals so QA can advance past them. Only the Escrow gate can be bypassed today — this clears Underwriting §2, Closing Step 3, and the escrow half of the boarding gate; other closing gates still apply."
      />

      <Card>
        <header className="flex items-start gap-space-2 mb-space-3">
          <ShieldAlert
            size={18}
            className="text-status-warning mt-[1px] shrink-0"
            aria-hidden
          />
          <div>
            <h3 className="font-display text-h4 text-onyx m-0">
              Bypass a deal&apos;s escrow gate
            </h3>
            <p className="text-caption text-gray-mid m-0">
              Every bypass is audit-logged and visible to compliance reviewers.
              Use it only on test deals.
            </p>
          </div>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 mb-space-3">
          <FormField
            label="Deal ID (GUID)"
            required
            error={
              dealIdTouched && !dealIdValid ? "Enter a valid GUID." : undefined
            }
          >
            <Input
              value={dealId}
              onChange={(e) => setDealId(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              invalid={dealIdTouched && !dealIdValid}
              autoComplete="off"
              spellCheck={false}
            />
          </FormField>
          <FormField label="Reason (audit-visible)" required>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={`Why this bypass is necessary. Min. ${MIN_REASON_LENGTH} chars.`}
            />
          </FormField>
        </div>

        {blocked && (
          <p className="text-small text-gray-mid mt-0 mb-space-3" data-testid="console-bypass-blocked">
            {blocked}
          </p>
        )}
        <div className="flex justify-end">
          <Button
            variant="destructive"
            onClick={() => void onSubmit()}
            disabled={
              !dealIdValid ||
              !reasonValid ||
              bypass.isPending ||
              escrow.isLoading ||
              blocked !== null
            }
            loading={bypass.isPending}
            data-testid="console-bypass-submit"
          >
            Bypass escrow gate
          </Button>
        </div>
      </Card>

      <Card>
        <header className="mb-space-3">
          <h3 className="font-display text-h4 text-onyx m-0">Recent bypasses</h3>
          <p className="text-caption text-gray-mid m-0">
            Cross-deal, most recent first.
          </p>
        </header>

        {recent.isLoading ? (
          <p className="text-small text-gray-mid italic m-0">Loading…</p>
        ) : rows.length === 0 ? (
          <EmptyState
            title="No bypasses yet"
            description="Bypassed test deals will appear here."
          />
        ) : (
          <Table data-testid="recent-bypass-table">
            <THead>
              <TR>
                <TH>Deal</TH>
                <TH>Gate</TH>
                <TH>Actor</TH>
                <TH>Reason</TH>
                <TH>When</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((row) => (
                <TR key={row.id}>
                  <TD>
                    <Link
                      to={`/deal-flow/deals/${row.dealId}`}
                      className="text-gold-shadow hover:underline"
                    >
                      {row.dealNumber ?? row.dealId}
                    </Link>
                  </TD>
                  <TD>
                    <Badge status="review">{row.gateCode}</Badge>
                  </TD>
                  <TD>{row.actorDisplayName ?? row.actorUserId}</TD>
                  <TD className="max-w-[28rem] whitespace-normal break-words">
                    {row.reason}
                  </TD>
                  <TD className="whitespace-nowrap text-gray-mid">
                    {relative(row.occurredAt)}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
